/**
 * Fase 7 — fiscal: contratos entre a API e a tela.
 *
 * Aqui NÃO há tributação: só os nomes dos campos, os formatos aceitos e os tipos do
 * resultado do motor. CFOP, CST, alíquotas e reduções vêm da matriz cadastrada pelo usuário
 * (tabelas tax_rules/tax_rule_versions); o cálculo roda só na API (motor fiscal único).
 */
import { z } from "zod";
import { parseDecimalBR, type DecimalMode } from "./decimal-input";
import {
  ICMS_CSOSN,
  ICMS_CST,
  ICMS_MOD_BC,
  ICMS_MOD_BC_ST,
  ICMS_MOT_DES,
  IPI_CST,
  PIS_COFINS_CST,
  domainCodes,
  type DomainOption,
} from "./fiscal-domains";

// ---------------------------------------------------------------- vocabulário
export const FISCAL_ENVIRONMENTS = ["simulador", "homologacao", "producao"] as const;
export type FiscalEnvironment = (typeof FISCAL_ENVIRONMENTS)[number];
export const FISCAL_ENVIRONMENT_LABEL: Record<FiscalEnvironment, string> = {
  simulador: "Simulador (sem valor fiscal)",
  homologacao: "Homologação (testes na SEFAZ)",
  producao: "Produção",
};

export const FISCAL_PROVIDERS = ["desligado", "simulado", "focus_nfe"] as const;
export type FiscalProviderCode = (typeof FISCAL_PROVIDERS)[number];
export const FISCAL_PROVIDER_LABEL: Record<FiscalProviderCode, string> = {
  desligado: "Desligado",
  simulado: "Simulador interno",
  focus_nfe: "Focus NFe",
};

export const SIMULATOR_SCENARIOS = [
  "autorizar",
  "rejeitar",
  "denegar",
  "incerto_autoriza",
  "incerto_nao_recebido",
  "indisponivel",
] as const;
export type SimulatorScenario = (typeof SIMULATOR_SCENARIOS)[number];
export const SIMULATOR_SCENARIO_LABEL: Record<SimulatorScenario, string> = {
  autorizar: "Autoriza",
  rejeitar: "Rejeita (para testar correção e reenvio)",
  denegar: "Denega",
  incerto_autoriza: "Sem resposta; a consulta encontra a nota autorizada",
  incerto_nao_recebido: "Sem resposta; a consulta mostra que não recebeu (reenvia)",
  indisponivel: "Provedor fora do ar (tenta de novo depois)",
};

export const FISCAL_SYSTEM_TYPES = [
  "venda",
  "remessa_venda_fora",
  "retorno_remessa",
  "devolucao_venda",
  "divergencia_carga",
  "outra",
] as const;
export type FiscalSystemType = (typeof FISCAL_SYSTEM_TYPES)[number];
export const FISCAL_SYSTEM_TYPE_LABEL: Record<FiscalSystemType, string> = {
  venda: "Venda",
  remessa_venda_fora: "Remessa para venda fora do estabelecimento",
  retorno_remessa: "Retorno de remessa",
  devolucao_venda: "Devolução de venda",
  divergencia_carga: "Divergência de carga",
  outra: "Outra",
};

/** Pontos do sistema que emitem documento fiscal (cada um aponta para uma operação). */
export const FISCAL_USES = [
  "venda_fabrica",
  "venda_rota",
  "remessa_carga",
  "retorno_carga",
  "divergencia_falta",
  "divergencia_sobra",
  "devolucao_venda",
] as const;
export type FiscalUse = (typeof FISCAL_USES)[number];
export const FISCAL_USE_LABEL: Record<FiscalUse, string> = {
  venda_fabrica: "Venda na fábrica",
  venda_rota: "Venda em rota (celular do vendedor)",
  remessa_carga: "Remessa da carga",
  retorno_carga: "Retorno da carga (acerto)",
  divergencia_falta: "Falta na carga (acerto)",
  divergencia_sobra: "Sobra na carga (acerto)",
  devolucao_venda: "Devolução de venda",
};
export const FISCAL_USE_SYSTEM_TYPE: Record<FiscalUse, FiscalSystemType> = {
  venda_fabrica: "venda",
  venda_rota: "venda",
  remessa_carga: "remessa_venda_fora",
  retorno_carga: "retorno_remessa",
  divergencia_falta: "divergencia_carga",
  divergencia_sobra: "divergencia_carga",
  devolucao_venda: "devolucao_venda",
};

export const TAX_KINDS = [
  "cfop",
  "icms",
  "icms_st_fcp",
  "pis",
  "cofins",
  "ipi",
  "ibs_cbs",
  "info_adicional",
] as const;
export type TaxKind = (typeof TAX_KINDS)[number];
export const TAX_KIND_LABEL: Record<TaxKind, string> = {
  cfop: "CFOP",
  icms: "ICMS",
  icms_st_fcp: "ICMS-ST / FCP",
  pis: "PIS",
  cofins: "COFINS",
  ipi: "IPI",
  ibs_cbs: "IBS / CBS",
  info_adicional: "Informações adicionais",
};

/** Tributos calculados por item (linhas de fiscal_item_taxes). */
export const TAX_LINES = [
  "icms",
  "icms_deson",
  "icms_st",
  "fcp",
  "fcp_st",
  "difal",
  "pis",
  "cofins",
  "ipi",
  "ibs_uf",
  "ibs_mun",
  "cbs",
  "is",
] as const;
export type TaxLineCode = (typeof TAX_LINES)[number];
export const TAX_LINE_LABEL: Record<TaxLineCode, string> = {
  icms: "ICMS",
  icms_deson: "ICMS desonerado",
  icms_st: "ICMS-ST",
  fcp: "FCP",
  fcp_st: "FCP-ST",
  difal: "DIFAL",
  pis: "PIS",
  cofins: "COFINS",
  ipi: "IPI",
  ibs_uf: "IBS (UF)",
  ibs_mun: "IBS (município)",
  cbs: "CBS",
  is: "Imposto seletivo",
};

export const RULE_STATUSES = ["rascunho", "testada", "aprovada", "arquivada"] as const;
export type RuleStatus = (typeof RULE_STATUSES)[number];

/**
 * v7.1 — escopo da aprovação: "somente testes" vale no simulador e na homologação da SEFAZ;
 * "produção" vale também na emissão com validade jurídica. Pendência fiscal aberta impede
 * produção (o banco recusa).
 */
export const APPROVAL_SCOPES = ["homologacao", "producao"] as const;
export type ApprovalScope = (typeof APPROVAL_SCOPES)[number];
export const APPROVAL_SCOPE_LABEL: Record<ApprovalScope, string> = {
  homologacao: "Somente testes (simulador e homologação)",
  producao: "Produção",
};
/** Situação mostrada: vigente e encerrada derivam das datas de uma versão aprovada. */
export type RuleDisplayStatus = RuleStatus | "vigente" | "encerrada" | "futura";

export function ruleDisplayStatus(
  v: { status: RuleStatus; validFrom: string; validTo: string | null },
  today: string,
): RuleDisplayStatus {
  if (v.status !== "aprovada") return v.status;
  if (v.validFrom > today) return "futura";
  if (v.validTo !== null && v.validTo < today) return "encerrada";
  return "vigente";
}

export const FISCAL_DOCUMENT_STATUSES = [
  "pendente",
  "processando",
  "incerta",
  "contingencia",
  "autorizada",
  "rejeitada",
  "denegada",
  "cancelada",
  "abandonada",
  "inutilizada",
] as const;
export type FiscalDocumentStatus = (typeof FISCAL_DOCUMENT_STATUSES)[number];
export const FISCAL_DOCUMENT_STATUS_LABEL: Record<FiscalDocumentStatus, string> = {
  pendente: "Na fila",
  processando: "Processando",
  incerta: "Situação incerta (consultando)",
  contingencia: "Em contingência",
  autorizada: "Autorizada",
  rejeitada: "Rejeitada",
  denegada: "Denegada",
  cancelada: "Cancelada",
  abandonada: "Abandonada",
  inutilizada: "Número inutilizado",
};

// ---------------------------------------------------------------- formatos básicos
const UF_LIST = [
  "AC",
  "AL",
  "AP",
  "AM",
  "BA",
  "CE",
  "DF",
  "ES",
  "GO",
  "MA",
  "MT",
  "MS",
  "MG",
  "PA",
  "PB",
  "PR",
  "PE",
  "PI",
  "RJ",
  "RN",
  "RS",
  "RO",
  "RR",
  "SC",
  "SP",
  "SE",
  "TO",
] as const;
export const FISCAL_UFS = UF_LIST;
export type Uf = (typeof UF_LIST)[number];
const ufSchema = z.enum(UF_LIST);

/**
 * Número digitado no padrão brasileiro ("10.000,00", "12,5") ou com ponto ("12.5"): devolve o
 * texto com ponto que a API e o banco guardam, com os mesmos limites de antes.
 */
const decimalText = (pattern: RegExp, mode: DecimalMode, message: string) =>
  z.string().transform((v, ctx) => {
    const canonical = parseDecimalBR(v, mode);
    if (canonical === null || !pattern.test(canonical)) {
      ctx.addIssue({ code: "custom", message });
      return z.NEVER;
    }
    return canonical;
  });
/** Valor em reais (até 12 dígitos e 2 casas). */
const money = (message = "Informe um valor em reais, ex.: 10.000,00.") =>
  decimalText(/^\d{1,12}(\.\d{1,2})?$/, "dinheiro", message);
/** Quantidade ou preço unitário (até 6 casas). */
const qty6 = (message: string) => decimalText(/^\d{1,12}(\.\d{1,6})?$/, "numero", message);

const PCT_RE = /^\d{1,3}(\.\d{1,4})?$/;
const pct = decimalText(
  PCT_RE,
  "numero",
  "Informe o percentual, ex.: 12,5 (até 4 casas decimais).",
).refine((v) => Number(v) <= 100, "O percentual não pode passar de 100.");
const mva = decimalText(
  PCT_RE,
  "numero",
  "Informe a MVA, ex.: 40,5 (até 4 casas decimais).",
).refine((v) => Number(v) <= 999.9999, "A MVA não pode passar de 999,9999.");
const unitValue = decimalText(
  /^\d{1,8}(\.\d{1,6})?$/,
  "numero",
  "Informe o valor por unidade, ex.: 0,1234.",
);
const refCode = z.string().regex(/^[A-Z0-9][A-Z0-9_.-]{0,39}$/, "Código de referência inválido");
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === "" ? undefined : v))
    .optional();

// ---------------------------------------------------------------- parâmetros por grupo
export const cfopParamsSchema = z
  .object({
    cfop: z.string().regex(/^[1-7]\d{3}$/, "CFOP com 4 dígitos"),
    natureza: optionalText(60),
  })
  .strict();
export const icmsParamsSchema = z
  .object({
    cst: z
      .enum(
        domainCodes(ICMS_CST),
        "Escolha o CST do ICMS na lista (2 dígitos). A origem é um campo separado: 020 = origem 0 + CST 20.",
      )
      .optional(),
    csosn: z.enum(domainCodes(ICMS_CSOSN), "Escolha o CSOSN na lista.").optional(),
    modBC: z.enum(domainCodes(ICMS_MOD_BC), "Escolha a modalidade da base na lista.").optional(),
    pRedBC: pct.optional(),
    pICMS: pct.optional(),
    pDif: pct.optional(),
    motDesICMS: z
      .string()
      .regex(/^\d{1,2}$/, "Escolha o motivo da desoneração na lista.")
      .optional(),
    cBenef: z
      .string()
      .regex(/^[A-Z0-9]{8,10}$/, "Código de benefício com 8 a 10 caracteres")
      .optional(),
    incluirIpiNaBase: z.boolean().optional(),
    calcularDesoneracao: z.boolean().optional(),
  })
  .strict();
export const icmsStParamsSchema = z
  .object({
    modBCST: z
      .enum(domainCodes(ICMS_MOD_BC_ST), "Escolha a modalidade da base da ST na lista.")
      .optional(),
    pMVAST: mva.optional(),
    pRedBCST: pct.optional(),
    pICMSST: pct.optional(),
    pFCP: pct.optional(),
    pFCPST: pct.optional(),
    incluirIpiNaBaseST: z.boolean().optional(),
  })
  .strict();
export const pisCofinsParamsSchema = z
  .object({
    cst: z.string().regex(/^\d{2}$/, "Escolha o CST na lista (2 dígitos)."),
    pAliq: pct.optional(),
    vAliqProd: unitValue.optional(),
    excluirIcmsDaBase: z.boolean().optional(),
    natRec: z
      .string()
      .regex(/^\d{3}$/, "Natureza da receita com 3 dígitos.")
      .optional(),
  })
  .strict();
export const ipiParamsSchema = z
  .object({
    cst: z.enum(domainCodes(IPI_CST), "Escolha o CST do IPI na lista."),
    cEnq: z.string().regex(/^\d{3}$/, "Enquadramento com 3 dígitos"),
    pIPI: pct.optional(),
    vUnid: unitValue.optional(),
  })
  .strict();
export const ibsCbsParamsSchema = z
  .object({
    cst: z.string().regex(/^\d{3}$/, "CST com 3 dígitos"),
    cClassTrib: z.string().regex(/^\d{6}$/, "Classificação tributária com 6 dígitos"),
    ibsUf: pct.optional(),
    ibsMun: pct.optional(),
    cbs: pct.optional(),
    ibsUfRef: refCode.optional(),
    ibsMunRef: refCode.optional(),
    cbsRef: refCode.optional(),
    pRedIbs: pct.optional(),
    pRedCbs: pct.optional(),
    excluirDaBase: z.array(z.enum(["icms", "pis", "cofins"])).optional(),
  })
  .strict();
export const infoParamsSchema = z
  .object({
    textoFisco: optionalText(2000),
    textoContribuinte: optionalText(5000),
  })
  .strict();

export const TAX_PARAM_SCHEMAS = {
  cfop: cfopParamsSchema,
  icms: icmsParamsSchema,
  icms_st_fcp: icmsStParamsSchema,
  pis: pisCofinsParamsSchema,
  cofins: pisCofinsParamsSchema,
  ipi: ipiParamsSchema,
  ibs_cbs: ibsCbsParamsSchema,
  info_adicional: infoParamsSchema,
} satisfies Record<TaxKind, z.ZodType>;

/**
 * Campos de cada grupo na tela da regra (os nomes do leiaute; nenhum valor padrão).
 * `select`: lista fechada com código + descrição (grava só o código); `numeric`: aceita
 * vírgula (a API converte); os demais textos são gravados exatamente como digitados.
 */
export type TaxParamField = {
  key: string;
  label: string;
  type: "text" | "bool" | "list" | "select";
  options?: readonly DomainOption[];
  listOptions?: readonly string[];
  numeric?: boolean;
  hint?: string;
};
export const TAX_PARAM_FIELDS: Record<TaxKind, TaxParamField[]> = {
  cfop: [
    { key: "cfop", label: "CFOP", type: "text", hint: "4 dígitos" },
    { key: "natureza", label: "Natureza da operação (opcional)", type: "text" },
  ],
  icms: [
    {
      key: "cst",
      label: "CST",
      type: "select",
      options: ICMS_CST,
      hint: "A origem da mercadoria é escolhida à parte (020 no DANFE = origem 0 + CST 20)",
    },
    { key: "csosn", label: "CSOSN (Simples)", type: "select", options: ICMS_CSOSN },
    { key: "modBC", label: "Modalidade da base", type: "select", options: ICMS_MOD_BC },
    { key: "pICMS", label: "Alíquota %", type: "text", numeric: true },
    { key: "pRedBC", label: "Redução da base %", type: "text", numeric: true },
    { key: "pDif", label: "Diferimento %", type: "text", numeric: true },
    { key: "cBenef", label: "Código do benefício (cBenef)", type: "text" },
    {
      key: "motDesICMS",
      label: "Motivo da desoneração",
      type: "select",
      options: ICMS_MOT_DES,
    },
    { key: "calcularDesoneracao", label: "Calcular ICMS desonerado", type: "bool" },
    { key: "incluirIpiNaBase", label: "IPI na base do ICMS", type: "bool" },
  ],
  icms_st_fcp: [
    {
      key: "modBCST",
      label: "Modalidade da base ST",
      type: "select",
      options: ICMS_MOD_BC_ST,
    },
    { key: "pMVAST", label: "MVA %", type: "text", numeric: true },
    { key: "pRedBCST", label: "Redução da base ST %", type: "text", numeric: true },
    { key: "pICMSST", label: "Alíquota ST %", type: "text", numeric: true },
    { key: "pFCP", label: "FCP %", type: "text", numeric: true },
    { key: "pFCPST", label: "FCP-ST %", type: "text", numeric: true },
    { key: "incluirIpiNaBaseST", label: "IPI na base da ST", type: "bool" },
  ],
  pis: [
    { key: "cst", label: "CST", type: "select", options: PIS_COFINS_CST },
    { key: "pAliq", label: "Alíquota %", type: "text", numeric: true },
    { key: "vAliqProd", label: "Valor por unidade", type: "text", numeric: true },
    { key: "natRec", label: "Natureza da receita", type: "text", hint: "3 dígitos" },
    { key: "excluirIcmsDaBase", label: "Excluir o ICMS da base", type: "bool" },
  ],
  cofins: [
    { key: "cst", label: "CST", type: "select", options: PIS_COFINS_CST },
    { key: "pAliq", label: "Alíquota %", type: "text", numeric: true },
    { key: "vAliqProd", label: "Valor por unidade", type: "text", numeric: true },
    { key: "natRec", label: "Natureza da receita", type: "text", hint: "3 dígitos" },
    { key: "excluirIcmsDaBase", label: "Excluir o ICMS da base", type: "bool" },
  ],
  ipi: [
    { key: "cst", label: "CST", type: "select", options: IPI_CST },
    { key: "cEnq", label: "Enquadramento", type: "text", hint: "3 dígitos" },
    { key: "pIPI", label: "Alíquota %", type: "text", numeric: true },
    { key: "vUnid", label: "Valor por unidade", type: "text", numeric: true },
  ],
  ibs_cbs: [
    { key: "cst", label: "CST (3 dígitos)", type: "text", hint: "Tabela da reforma tributária" },
    {
      key: "cClassTrib",
      label: "Classificação tributária",
      type: "text",
      hint: "6 dígitos (cClassTrib)",
    },
    { key: "ibsUf", label: "IBS UF %", type: "text", numeric: true },
    { key: "ibsUfRef", label: "ou tabela de referência do IBS UF", type: "text" },
    { key: "ibsMun", label: "IBS município %", type: "text", numeric: true },
    { key: "ibsMunRef", label: "ou tabela do IBS município", type: "text" },
    { key: "cbs", label: "CBS %", type: "text", numeric: true },
    { key: "cbsRef", label: "ou tabela da CBS", type: "text" },
    { key: "pRedIbs", label: "Redução IBS %", type: "text", numeric: true },
    { key: "pRedCbs", label: "Redução CBS %", type: "text", numeric: true },
    {
      key: "excluirDaBase",
      label: "Excluir da base",
      type: "list",
      listOptions: ["icms", "pis", "cofins"],
    },
  ],
  info_adicional: [
    { key: "textoFisco", label: "Texto ao fisco", type: "text" },
    {
      key: "textoContribuinte",
      label: "Texto ao contribuinte",
      type: "text",
      hint: "Variáveis: {{total_icms}}, {{total_icms_st}}, {{total_ibs}}, {{total_cbs}}, {{total_documento}}",
    },
  ],
};

/** Rótulo de um parâmetro para mensagens ("CST", "Alíquota %"); a chave se não houver. */
export function taxParamLabel(kind: TaxKind, key: string): string {
  return TAX_PARAM_FIELDS[kind].find((f) => f.key === key)?.label ?? key;
}

export type TaxParams = Record<string, string | boolean | string[] | undefined>;

/** Parâmetros que tornam a regra completa (sem eles o motor bloqueia a emissão). */
export function ruleCompletenessIssues(kind: TaxKind, params: TaxParams): string[] {
  const out: string[] = [];
  const has = (k: string) => params[k] !== undefined && params[k] !== "";
  if (kind === "cfop" && !has("cfop")) out.push("Informe o CFOP.");
  if (kind === "icms") {
    if (!has("cst") && !has("csosn")) out.push("Informe o CST (ou CSOSN) do ICMS.");
    const taxed = ["00", "10", "20", "51", "70", "90"].includes(String(params["cst"] ?? ""));
    if (taxed && !has("modBC")) out.push("Informe a modalidade da base de cálculo.");
    if (taxed && !has("pICMS")) out.push("Informe a alíquota do ICMS.");
    if (["20", "70"].includes(String(params["cst"] ?? "")) && !has("pRedBC"))
      out.push("CST com redução de base exige o percentual de redução.");
    if (has("calcularDesoneracao") && params["calcularDesoneracao"] === true && !has("motDesICMS"))
      out.push("Desoneração exige o motivo (motDesICMS).");
  }
  if (kind === "icms_st_fcp" && !has("pICMSST") && !has("pFCP") && !has("pFCPST"))
    out.push("Informe ao menos a alíquota de ST, de FCP ou de FCP-ST.");
  if (kind === "icms_st_fcp" && has("pICMSST") && !has("modBCST"))
    out.push("Informe a modalidade da base de cálculo da ST.");
  if ((kind === "pis" || kind === "cofins") && !has("cst")) out.push("Informe o CST.");
  if (kind === "ipi" && (!has("cst") || !has("cEnq"))) out.push("Informe CST e enquadramento.");
  if (kind === "ibs_cbs") {
    if (!has("cst") || !has("cClassTrib")) out.push("Informe CST e classificação tributária.");
    for (const [rate, ref, label] of [
      ["ibsUf", "ibsUfRef", "IBS da UF"],
      ["ibsMun", "ibsMunRef", "IBS do município"],
      ["cbs", "cbsRef", "CBS"],
    ] as const) {
      if (!has(rate) && !has(ref))
        out.push(`Informe a alíquota ou a tabela de referência de ${label}.`);
      if (has(rate) && has(ref))
        out.push(`${label}: use a alíquota OU a tabela de referência, não as duas.`);
    }
  }
  if (kind === "info_adicional" && !has("textoFisco") && !has("textoContribuinte"))
    out.push("Informe ao menos um texto.");
  if ((kind === "pis" || kind === "cofins") && has("pAliq") && has("vAliqProd"))
    out.push("Use alíquota percentual OU valor por unidade.");
  if (kind === "ipi" && has("pIPI") && has("vUnid"))
    out.push("Use alíquota percentual OU valor por unidade.");
  return out;
}

// ---------------------------------------------------------------- condições da regra
const nullableEnum = <const T extends readonly [string, ...string[]]>(values: T) =>
  z.enum(values).nullable().default(null);
export const ruleConditionsSchema = z
  .object({
    operationId: z.uuid().nullable().default(null),
    productId: z.uuid().nullable().default(null),
    taxGroupId: z.uuid().nullable().default(null),
    ncmPrefix: z
      .string()
      .regex(/^\d{2,8}$/, "NCM ou prefixo com 2 a 8 dígitos")
      .nullable()
      .default(null),
    cest: z
      .string()
      .regex(/^\d{7}$/, "CEST com 7 dígitos")
      .nullable()
      .default(null),
    origin: nullableEnum(["0", "1", "2", "3", "4", "5", "6", "7", "8"]),
    ufOrigin: ufSchema.nullable().default(null),
    ufDest: ufSchema.nullable().default(null),
    destScope: nullableEnum(["interna", "interestadual"]),
    personKind: nullableEnum(["pf", "pj"]),
    ieIndicator: nullableEnum(["1", "2", "9"]),
    finalConsumer: z.boolean().nullable().default(null),
    ruralProducer: z.boolean().nullable().default(null),
    model: nullableEnum(["55", "65"]),
    purpose: nullableEnum(["1", "2", "3", "4"]),
  })
  .refine((c) => !(c.ufDest && c.destScope), {
    message:
      "Informe apenas uma das condições: UF de destino ou tipo da operação (interna/interestadual).",
    path: ["destScope"],
  });
export type RuleConditions = z.output<typeof ruleConditionsSchema>;

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Data inválida");

export const taxRuleCreateSchema = z.object({
  kind: z.enum(TAX_KINDS),
  code: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z0-9][A-Z0-9_.-]{0,39}$/, "Código com letras, números, ponto, hífen ou _"),
  name: z.string().trim().min(2, "Informe o nome").max(120),
  description: optionalText(2000),
});
export const taxRuleVersionInputSchema = z.object({
  validFrom: isoDate,
  validTo: isoDate.nullable().default(null),
  conditions: ruleConditionsSchema,
  params: z.record(z.string(), z.unknown()),
  notes: optionalText(2000),
});
export type TaxRuleVersionInput = z.input<typeof taxRuleVersionInputSchema>;

export type RuleVersionView = {
  id: string;
  ruleId: string;
  ruleCode: string;
  ruleName: string;
  kind: TaxKind;
  versionNo: number;
  status: RuleStatus;
  displayStatus: RuleDisplayStatus;
  validFrom: string;
  validTo: string | null;
  conditions: RuleConditions;
  params: TaxParams;
  notes: string | null;
  createdByEmail: string | null;
  preparedByEmail: string | null;
  testedByEmail: string | null;
  testedAt: string | null;
  testReport: TestReport | null;
  approvedByEmail: string | null;
  approvedAt: string | null;
  approvalNote: string | null;
  /** aprovada por quem a preparou (permissão fiscal.matriz.aprovar_propria) */
  selfApproved: boolean;
  /** v7.1: escopo da aprovação (null enquanto não aprovada) */
  approvalScope: ApprovalScope | null;
  productionApprovedByEmail: string | null;
  productionApprovedAt: string | null;
  validationResponsible: string | null;
  validationCrc: string | null;
  validationDate: string | null;
  closedByEmail: string | null;
  closedAt: string | null;
  closeReason: string | null;
  version: number;
};

export type TestReport = {
  passed: boolean;
  cases: { id: string; name: string; passed: boolean; differences: string[] }[];
  conflicts: string[];
  completeness: string[];
  ranAt: string;
};

// ---------------------------------------------------------------- simulação / cálculo
export const recipientProfileSchema = z.object({
  uf: ufSchema,
  personKind: z.enum(["pf", "pj"]),
  ieIndicator: z.enum(["1", "2", "9"]),
  finalConsumer: z.boolean(),
  ruralProducer: z.boolean().default(false),
  name: z.string().trim().max(120).default("Destinatário avulso"),
});

export const simulationInputSchema = z
  .object({
    operationId: z.uuid(),
    customerId: z.uuid().nullable().default(null),
    recipient: recipientProfileSchema.nullable().default(null),
    date: isoDate.optional(),
    model: z.enum(["55", "65"]).nullable().default(null),
    includeDrafts: z.boolean().default(false),
    items: z
      .array(
        z.object({
          productId: z.uuid(),
          quantity: qty6("Informe a quantidade, ex.: 1,5."),
          unitPrice: qty6("Informe o preço, ex.: 40,50."),
          discount: money().default("0"),
          freight: money().default("0"),
          other: money().default("0"),
        }),
      )
      .min(1, "Inclua ao menos um produto")
      .max(100),
  })
  .refine((v) => (v.customerId === null) !== (v.recipient === null), {
    message: "Escolha um cliente OU descreva um destinatário avulso",
    path: ["customerId"],
  });
export type SimulationInput = z.input<typeof simulationInputSchema>;

export type IssueContext = {
  operation?: string;
  ufOrigin?: string | null;
  ufDest?: string | null;
  ncm?: string | null;
  product?: string;
  recipient?: string;
  kind?: TaxKind;
  date?: string;
};
export type CalcIssue = {
  code: string;
  message: string;
  itemLine?: number;
  kind?: TaxKind;
  context?: IssueContext;
  /** regras próximas (rascunho, fora da vigência, condição quase igual) — ajudam a configurar */
  nearest?: string[];
};

export type SelectedRule = {
  versionId: string;
  ruleId: string;
  ruleCode: string;
  ruleName: string;
  versionNo: number;
  status: RuleStatus;
  /** v7.1: escopo da aprovação da versão escolhida (null = rascunho/testada) */
  approvalScope?: ApprovalScope | null;
  validFrom: string;
  validTo: string | null;
  matched: string[];
  reason: string;
  params: TaxParams;
};
export type DiscardedRule = {
  versionId: string;
  ruleCode: string;
  versionNo: number;
  status: RuleStatus;
  reason: string;
};
export type GroupDecision = {
  kind: TaxKind;
  status: "aplicada" | "ausente" | "ambigua" | "nao_aplicavel";
  required: boolean;
  selected: SelectedRule | null;
  discarded: DiscardedRule[];
  ambiguous: string[];
};
export type TaxLine = {
  tax: TaxLineCode;
  cst: string | null;
  baseOriginal: string;
  reductionPercent: string;
  base: string;
  rate: string | null;
  ratePerUnit: string | null;
  amount: string;
  ruleVersionId: string | null;
  extra: Record<string, string | boolean | null>;
};
export type CalcItem = {
  line: number;
  productId: string;
  productCode: string;
  description: string;
  ncm: string | null;
  cest: string | null;
  origin: string;
  unitCode: string;
  quantity: string;
  unitPrice: string;
  gross: string;
  discount: string;
  freight: string;
  other: string;
  total: string;
  cfop: string | null;
  groups: GroupDecision[];
  taxes: TaxLine[];
};
export type CalcTotals = {
  products: string;
  discount: string;
  freight: string;
  other: string;
  taxes: Partial<Record<TaxLineCode, string>>;
  document: string;
};
export type CalcResult = {
  ok: boolean;
  mode: "simulacao" | "previa" | "emissao";
  date: string;
  model: "55" | "65" | null;
  modelReason: string;
  operation: { id: string; code: string; name: string; nature: string | null } | null;
  recipientLabel: string;
  destScope: "interna" | "interestadual" | null;
  items: CalcItem[];
  totals: CalcTotals;
  infoFisco: string | null;
  infoContribuinte: string | null;
  issues: CalcIssue[];
  warnings: string[];
  usedDrafts: boolean;
  hash: string;
};

// ---------------------------------------------------------------- casos de teste
export const testCaseInputSchema = z.object({
  name: z.string().trim().min(2).max(120),
  input: z.record(z.string(), z.unknown()),
  expected: z
    .object({
      totals: z.record(z.string(), z.string()).optional(),
      items: z
        .array(
          z.object({
            line: z.number().int().min(1),
            cfop: z.string().optional(),
            taxes: z.record(z.string(), z.string()).optional(),
          }),
        )
        .optional(),
    })
    .default({}),
});

// ---------------------------------------------------------------- configuração
const nullableText = (max: number, min = 1) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === "" ? null : v))
    .nullable()
    .default(null)
    .refine((v) => v === null || v.length >= min, `Mínimo de ${min} caracteres`);

/**
 * Segredos (token do provedor, segredo do webhook, CSRT) ficam em variáveis de ambiente do
 * SERVIDOR; o banco guarda só o NOME. O nome carrega o id da empresa
 * (FISCAL_SECRET_<id sem hífens, maiúsculo>_<NOME>): uma empresa nunca aponta para o
 * segredo de outra nem para variáveis de configuração comuns.
 */
export const SECRET_REF_RE = /^FISCAL_SECRET_[0-9A-F]{32}_[A-Z0-9_]{1,40}$/;
export const secretRefPrefix = (companyId: string) =>
  `FISCAL_SECRET_${companyId.replace(/-/g, "").toUpperCase()}_`;
export function isCompanySecretRef(
  companyId: string,
  ref: string | null | undefined,
): ref is string {
  return !!ref && SECRET_REF_RE.test(ref) && ref.startsWith(secretRefPrefix(companyId));
}

/** v7.1-hotfix2: conferência de uma referência de segredo (nunca o valor). */
export const SECRET_REF_STATUSES = [
  "formato_invalido",
  "outra_empresa",
  "ausente",
  "vazia",
  "encontrada",
] as const;
export type SecretRefStatus = (typeof SECRET_REF_STATUSES)[number];
export type SecretRefCheck = {
  status: SecretRefStatus;
  /** prefixo exigido para esta empresa: FISCAL_SECRET_<id da empresa sem hífens>_ */
  expectedPrefix: string;
  /** pasta do projeto de onde a API leu o .env (só o nome da pasta), ou null sem arquivo */
  envFolder: string | null;
  /** o .env foi alterado depois que a API o leu: é preciso reiniciar a API e o worker */
  envChangedSinceStart: boolean;
};
export const secretRefQuerySchema = z.object({
  ref: z.string().trim().min(1).max(120),
});

export const fiscalSettingsSchema = z.object({
  environment: z.enum(FISCAL_ENVIRONMENTS),
  provider: z.enum(FISCAL_PROVIDERS),
  simulatorScenario: z.enum(SIMULATOR_SCENARIOS).default("autorizar"),
  providerTokenRef: z
    .string()
    .regex(SECRET_REF_RE, "Nome da variável do servidor: FISCAL_SECRET_<empresa>_…")
    .nullable()
    .default(null),
  webhookSecretRef: z
    .string()
    .regex(SECRET_REF_RE, "Nome da variável do servidor: FISCAL_SECRET_<empresa>_…")
    .nullable()
    .default(null),
  pisCofinsRegime: z.enum(["cumulativo", "nao_cumulativo"]).nullable().default(null),
  ipiContributor: z.boolean().nullable().default(null),
  cnae: z
    .string()
    .regex(/^\d{7}$/, "CNAE com 7 dígitos")
    .nullable()
    .default(null),
  xmlEmail: z.email().toLowerCase().nullable().default(null),
  nfceCscId: z
    .string()
    .regex(/^\d{1,6}$/, "Identificador do CSC (número)")
    .nullable()
    .default(null),
  techRespCnpj: z
    .string()
    .regex(/^\d{14}$/, "CNPJ com 14 dígitos")
    .nullable()
    .default(null),
  techRespContact: nullableText(60, 2),
  techRespEmail: z.email().toLowerCase().nullable().default(null),
  techRespPhone: z
    .string()
    .regex(/^\d{6,14}$/, "Telefone só com números")
    .nullable()
    .default(null),
  csrtId: z
    .string()
    .regex(/^\d{2}$/, "ID do CSRT com 2 dígitos")
    .nullable()
    .default(null),
  csrtRef: z
    .string()
    .regex(SECRET_REF_RE, "Nome da variável do servidor: FISCAL_SECRET_<empresa>_…")
    .nullable()
    .default(null),
  requireLoadRemittance: z.boolean().default(false),
  routeNfceEnabled: z.boolean().default(false),
  deliveryUnderRemittance: z.boolean().default(false),
  emailCustomer: z.boolean().default(false),
  /** passagem para produção: o usuário digita PRODUCAO para confirmar */
  confirmProduction: z.string().optional(),
  version: z.number().int().optional(),
});
export type FiscalSettingsInput = z.input<typeof fiscalSettingsSchema>;
export type FiscalSettings = Omit<z.output<typeof fiscalSettingsSchema>, "confirmProduction"> & {
  productionEnabledByEmail: string | null;
  productionEnabledAt: string | null;
  version: number;
  exists: boolean;
  /** as variáveis referenciadas existem no servidor? (nunca o valor) */
  secretsPresent: { providerToken: boolean; webhookSecret: boolean; csrt: boolean };
};

export const fiscalOperationSchema = z.object({
  code: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z0-9][A-Z0-9_-]{0,19}$/, "Código com letras, números, - ou _"),
  name: z.string().trim().min(2).max(80),
  systemType: z.enum(FISCAL_SYSTEM_TYPES),
  nature: nullableText(60, 2),
  direction: z.enum(["saida", "entrada"]).nullable().default(null),
  purpose: z.enum(["1", "2", "3", "4"]).nullable().default(null),
  presence: z.enum(["0", "1", "2", "3", "4", "5", "9"]).nullable().default(null),
  finalConsumerMode: z.enum(["cliente", "sim", "nao"]).nullable().default(null),
  allowedModels: z
    .array(z.enum(["55", "65"]))
    .max(2)
    .default([]),
  defaultModel: z.enum(["55", "65"]).nullable().default(null),
  referenceRequired: z
    .enum(["nenhuma", "remessa_da_carga", "documento_original"])
    .default("nenhuma"),
  auxiliaryLayout: z
    .enum(["danfe", "danfe_simplificado", "danfe_etiqueta", "danfce"])
    .nullable()
    .default(null),
  emissionFlow: z.enum(["previa_confirmacao", "automatica"]).default("previa_confirmacao"),
  paymentMode: z.enum(["da_venda", "sem_pagamento"]).nullable().default(null),
  itemValueSource: z.enum(["preco_venda", "custo_medio", "preco_tabela"]).nullable().default(null),
  notes: nullableText(1000),
  status: z.enum(["active", "inactive"]).default("active"),
  version: z.number().int().optional(),
});
export type FiscalOperationInput = z.input<typeof fiscalOperationSchema>;
export type FiscalOperation = Omit<z.output<typeof fiscalOperationSchema>, "version"> & {
  id: string;
  version: number;
  missing: string[];
};

export const FISCAL_PRESENCE_LABEL: Record<string, string> = {
  "0": "Não se aplica",
  "1": "Presencial",
  "2": "Internet",
  "3": "Teleatendimento",
  "4": "Entrega a domicílio (NFC-e)",
  "5": "Presencial fora do estabelecimento",
  "9": "Outros",
};
export const FISCAL_PURPOSE_LABEL: Record<string, string> = {
  "1": "Normal",
  "2": "Complementar",
  "3": "Ajuste",
  "4": "Devolução",
};

export const fiscalUfRuleSchema = z.object({
  uf: ufSchema,
  validFrom: isoDate,
  validTo: isoDate.nullable().default(null),
  nfceOutsideEstablishment: z.boolean().default(false),
  nfceForContributor: z.boolean().default(false),
  nfceMaxAmountUnidentified: money().nullable().default(null),
  nfceCancelMinutes: z.number().int().min(1).max(100000).nullable().default(null),
  nfeCancelHours: z.number().int().min(1).max(10000).nullable().default(null),
  requiresTechResp: z.boolean().default(false),
  requiresCsrt: z.boolean().default(false),
  requiresPaymentLink: z.boolean().default(false),
  ibsCbsRequired: z.boolean().default(false),
  notes: nullableText(1000),
  version: z.number().int().optional(),
});
export type FiscalUfRuleInput = z.input<typeof fiscalUfRuleSchema>;
export type FiscalUfRule = Omit<z.output<typeof fiscalUfRuleSchema>, "version"> & {
  id: string;
  version: number;
};

export const fiscalSeriesSchema = z.object({
  model: z.enum(["55", "65"]),
  environment: z.enum(FISCAL_ENVIRONMENTS),
  series: z.number().int().min(0).max(999),
  nextNumber: z.number().int().min(1).max(999999999),
  status: z.enum(["active", "inactive"]).default("active"),
  version: z.number().int().optional(),
});

export const paymentInstitutionSchema = z.object({
  name: z.string().trim().min(2).max(80),
  cnpj: z.string().regex(/^\d{14}$/, "CNPJ com 14 dígitos"),
  uf: ufSchema.nullable().default(null),
  kind: z.enum(["credenciadora", "psp_pix", "banco", "outro"]),
  status: z.enum(["active", "inactive"]).default("active"),
  version: z.number().int().optional(),
});
export const paymentTerminalSchema = z.object({
  institutionId: z.uuid(),
  identifier: z.string().regex(/^[A-Za-z0-9._-]{1,40}$/, "Identificador do terminal"),
  label: nullableText(60),
  status: z.enum(["active", "inactive"]).default("active"),
  version: z.number().int().optional(),
});
export const paymentMethodFiscalSchema = z.object({
  electronic: z.boolean(),
  integrationType: z.enum(["1", "2"]).nullable().default(null),
  defaultInstitutionId: z.uuid().nullable().default(null),
  cardBrand: z
    .string()
    .regex(/^\d{2}$/)
    .nullable()
    .default(null),
  requiresAuthorization: z.boolean().default(false),
  requiresTransactionId: z.boolean().default(false),
  version: z.number().int().optional(),
});

/** Bandeiras (tBand) mais comuns; o campo aceita qualquer código de 2 dígitos do leiaute. */
export const CARD_BRANDS: Record<string, string> = {
  "01": "Visa",
  "02": "Mastercard",
  "03": "American Express",
  "04": "Sorocred",
  "05": "Diners",
  "06": "Elo",
  "07": "Hipercard",
  "08": "Aura",
  "09": "Cabal",
  "99": "Outros",
};

/**
 * v7.1: identificador da transação PIX (TxId), como vem do banco/PSP: letras e números, até 35.
 * É opcional e NUNCA é o valor (o valor do pagamento é o total da venda).
 */
export const PIX_TXID_RE = /^[A-Za-z0-9]{1,35}$/;

export const salePaymentDetailSchema = z.object({
  paymentMethodId: z.uuid(),
  amount: money(),
  integrationType: z.enum(["1", "2"]).nullable().default(null),
  institutionId: z.uuid().nullable().default(null),
  cardBrand: z
    .string()
    .regex(/^\d{2}$/)
    .nullable()
    .default(null),
  authorizationCode: z
    .string()
    .regex(/^[A-Za-z0-9._/-]{1,128}$/)
    .nullable()
    .default(null),
  nsu: z
    .string()
    .regex(/^[A-Za-z0-9]{1,40}$/)
    .nullable()
    .default(null),
  pixTxid: z
    .string()
    .regex(
      PIX_TXID_RE,
      "Identificador da transação PIX (TxId): só letras e números, até 35 — não é o valor pago.",
    )
    .nullable()
    .default(null),
  pixEndToEnd: z
    .string()
    .regex(/^[A-Za-z0-9]{32}$/, "EndToEndId com 32 caracteres")
    .nullable()
    .default(null),
  terminalId: z
    .string()
    .regex(/^[A-Za-z0-9._-]{1,40}$/)
    .nullable()
    .default(null),
  paidAt: z.iso.datetime({ offset: true }).nullable().default(null),
  notes: nullableText(300),
});
export type SalePaymentDetailInput = z.input<typeof salePaymentDetailSchema>;
/** Dado de pagamento gravado na venda (o que a nota leva em detPag). */
export type SalePaymentDetailView = {
  id: string;
  paymentMethodId: string;
  paymentMethodName: string;
  tpag: string;
  amount: string;
  integrationType: string | null;
  institutionId: string | null;
  institutionCnpj: string | null;
  cardBrand: string | null;
  authorizationCode: string | null;
  nsu: string | null;
  pixTxid: string | null;
  pixEndToEnd: string | null;
  terminalId: string | null;
  paidAt: string | null;
  notes: string | null;
};

// ---------------------------------------------------------------- prontidão
export type ReadinessItem = {
  key: string;
  label: string;
  ok: boolean;
  /** sem isto nenhum documento deste ponto sai (senão, só parte dos casos fica bloqueada) */
  blocking: boolean;
  detail?: string;
  /** seção de Fiscal → Configurações fiscais que resolve */
  section: string;
};
export type UseReadiness = {
  use: FiscalUse;
  label: string;
  operation: { id: string; code: string; name: string } | null;
  state: "pronta" | "incompleta" | "bloqueada";
  items: ReadinessItem[];
};
export type FiscalReadiness = {
  environment: FiscalEnvironment;
  provider: FiscalProviderCode;
  general: ReadinessItem[];
  uses: UseReadiness[];
  coverage: CoverageGap[];
};
export type CoverageGap = {
  use: FiscalUse;
  product: string;
  ncm: string | null;
  recipient: string;
  kind: TaxKind;
  problem: "ausente" | "ambigua";
  detail: string;
};

// ---------------------------------------------------------------- documentos
export const FISCAL_SOURCES = ["fabrica", "celular", "integracao"] as const;
export type FiscalSource = (typeof FISCAL_SOURCES)[number];

/** Origem de negócio de um documento fiscal: o ponto de emissão e o registro que o gera. */
export const fiscalSourceSchema = z.object({
  use: z.enum(FISCAL_USES),
  /** venda, carregamento, acerto ou devolução (conforme o ponto de emissão) */
  sourceId: z.uuid(),
  model: z.enum(["55", "65"]).nullable().default(null),
});
export type FiscalSourceInput = z.input<typeof fiscalSourceSchema>;
export const fiscalEmitSchema = fiscalSourceSchema.extend({
  /** hash da prévia que o usuário conferiu; diferente do recalculado = pedir nova confirmação */
  previewHash: z.string().regex(/^[0-9a-f]{64}$/, "Prévia inválida"),
  /** intenção (celular ou tela): a mesma intenção nunca gera dois documentos */
  intentKey: z.uuid().optional(),
  deviceId: z
    .string()
    .regex(/^[A-Za-z0-9._-]{8,64}$/)
    .optional(),
});
export type FiscalEmitInput = z.input<typeof fiscalEmitSchema>;

export const cancelSchema = z.object({
  reason: z.string().trim().min(15, "Motivo com pelo menos 15 caracteres").max(255),
});
export const cceSchema = z.object({
  correction: z.string().trim().min(15, "Correção com pelo menos 15 caracteres").max(1000),
});
export const abandonSchema = z.object({
  reason: z.string().trim().min(15, "Motivo com pelo menos 15 caracteres").max(255),
});
export const inutilizeSchema = z.object({
  model: z.enum(["55", "65"]),
  environment: z.enum(FISCAL_ENVIRONMENTS),
  series: z.number().int().min(0).max(999),
  numberFrom: z.number().int().min(1),
  numberTo: z.number().int().min(1),
  reason: z.string().trim().min(15).max(255),
});
export const printRecordSchema = z.object({
  transport: z.enum(["web_serial", "web_bluetooth", "rawbt", "pdf", "compartilhar", "navegador"]),
  result: z.enum(["sucesso", "falha"]),
  reprint: z.boolean().default(false),
  error: z.string().max(500).nullable().default(null),
  deviceId: z
    .string()
    .regex(/^[A-Za-z0-9._-]{8,64}$/)
    .nullable()
    .default(null),
});
export const cancelRequestDecisionSchema = z.object({
  decision: z.enum(["atender", "recusar"]),
  note: z.string().trim().max(500).optional(),
});

export type FiscalDocumentSummary = {
  id: string;
  model: "55" | "65";
  environment: FiscalEnvironment;
  series: number;
  number: number;
  accessKey: string | null;
  status: FiscalDocumentStatus;
  useKey: FiscalUse;
  operationName: string;
  source: FiscalSource;
  issueDate: string;
  totalAmount: string;
  recipientName: string | null;
  salesOrderId: string | null;
  salesOrderNumber: string | null;
  loadId: string | null;
  loadNumber: string | null;
  sefazCode: string | null;
  sefazMessage: string | null;
  requestedByEmail: string | null;
  requestedAt: string;
  authorizedAt: string | null;
};
export type FiscalDocumentDetail = FiscalDocumentSummary & {
  provider: string;
  protocol: string | null;
  emissionType: string;
  statusReason: string | null;
  cancelReason: string | null;
  cancelledAt: string | null;
  abandonReason: string | null;
  qrCodeUrl: string | null;
  deviceId: string | null;
  intentKey: string | null;
  totals: CalcTotals;
  calc: CalcResult;
  header: Record<string, unknown>;
  items: (CalcItem & { id: string })[];
  payments: {
    line: number;
    tpag: string;
    amount: string;
    electronic: boolean;
    integrationType: string | null;
    institutionCnpj: string | null;
    cardBrand: string | null;
    authorizationCode: string | null;
    pixEndToEnd: string | null;
    terminalId: string | null;
  }[];
  references: { kind: string; accessKey: string; referencedDocumentId: string | null }[];
  events: {
    id: string;
    kind: string;
    sequence: number;
    status: string;
    reason: string | null;
    correction: string | null;
    protocol: string | null;
    sefazMessage: string | null;
    requestedByEmail: string | null;
    requestedAt: string;
  }[];
  attempts: {
    operation: string;
    outcome: string;
    sefazCode: string | null;
    message: string | null;
    startedAt: string;
    durationMs: number | null;
  }[];
  prints: {
    transport: string;
    result: string;
    reprint: boolean;
    error: string | null;
    printedByEmail: string | null;
    createdAt: string;
  }[];
  cancelRequests: {
    id: string;
    reason: string;
    status: string;
    requestedByEmail: string | null;
    requestedAt: string;
    decisionNote: string | null;
  }[];
  job: { status: string; attempts: number; nextRunAt: string; lastError: string | null } | null;
  files: { id: string; kind: string; fileName: string; contentType: string }[];
  can: { resend: boolean; abandon: boolean; cancel: boolean; cce: boolean; print: boolean };
  cancelDeadline: string | null;
};

// ---------------------------------------------------------------- impressão (layout neutro)
/** Linha do layout neutro: o driver (ESC/POS, PDF...) converte; nenhuma marca no código. */
export type PrintLine =
  | { t: "text"; text: string; align?: "left" | "center" | "right"; bold?: boolean; size?: 1 | 2 }
  | { t: "pair"; left: string; right: string; bold?: boolean }
  | { t: "rule" }
  | { t: "qr"; data: string }
  | { t: "feed"; lines: number };
export type PrintLayout = {
  title: string;
  widthChars: number;
  lines: PrintLine[];
};

// ---------------------------------------------------------------- devolução de venda
export const salesReturnCreateSchema = z.object({
  salesOrderId: z.uuid(),
  warehouseId: z.uuid().optional(),
  reason: z.string().trim().min(3).max(500),
  items: z
    .array(
      z.object({
        salesOrderItemId: z.uuid(),
        lotId: z.uuid().nullable().default(null),
        quantity: qty6("Informe a quantidade, ex.: 1,5."),
      }),
    )
    .min(1)
    .max(200),
});
export const salesReturnConfirmSchema = z
  .object({
    fiscalMode: z.enum(["emissao_propria", "nota_do_cliente", "sem_documento_fiscal"]),
    customerAccessKey: z
      .string()
      .regex(/^\d{44}$/, "Chave com 44 dígitos")
      .nullable()
      .default(null),
    version: z.number().int(),
  })
  .refine((v) => (v.fiscalMode === "nota_do_cliente") === (v.customerAccessKey !== null), {
    message: "A chave da NF-e do cliente é informada só no modo 'nota do cliente'",
    path: ["customerAccessKey"],
  });
export const salesReturnReverseSchema = z.object({
  reason: z.string().trim().min(3).max(500),
  version: z.number().int(),
});

// ---------------------------------------------------------------- rota (celular)
/** "Confirmar e emitir" da rota: a venda inteira + a emissão, numa operação idempotente. */
export const routeSaleSchema = z.object({
  loadId: z.uuid(),
  customerId: z.uuid(),
  paymentMethodId: z.uuid(),
  items: z
    .array(
      z.object({
        productId: z.uuid(),
        unitId: z.uuid(),
        quantity: qty6("Informe a quantidade, ex.: 1,5."),
        /** vazio = preço da tabela do cliente (o celular sem conexão não conhece o preço final) */
        unitPrice: qty6("Informe o preço, ex.: 40,50.").nullable().default(null),
        discount: money().default("0"),
      }),
    )
    .min(1)
    .max(100),
  payment: salePaymentDetailSchema
    .omit({ paymentMethodId: true, amount: true })
    .nullable()
    .default(null),
  installments: z
    .array(
      z.object({
        dueDate: isoDate,
        amount: money(),
      }),
    )
    .max(60)
    .default([]),
  model: z.enum(["55", "65"]).nullable().default(null),
  deviceId: z.string().regex(/^[A-Za-z0-9._-]{8,64}$/),
  notes: z.string().max(1000).nullable().default(null),
  /** emitir a nota junto (a venda é registrada mesmo se a emissão estiver bloqueada) */
  emit: z.boolean().default(true),
  /** hash da prévia mostrada no celular (quando havia conexão) */
  previewHash: z
    .string()
    .regex(/^[0-9a-f]{64}$/)
    .nullable()
    .default(null),
});
export type RouteSaleInput = z.input<typeof routeSaleSchema>;

/** Resultado do "confirmar e emitir" da rota. */
export type RouteSaleResult = {
  orderId: string;
  orderNumber: string;
  orderStatus: string;
  totalAmount: string;
  fiscal:
    | { state: "emitido"; documentId: string; status: FiscalDocumentStatus; number: number }
    | { state: "bloqueado"; issues: CalcIssue[] }
    | { state: "desligado"; message: string }
    | { state: "nao_pedido" };
};

// ---------------------------------------------------------------- prévia (tela da fábrica e celular)
export type PreviewPayment = {
  line: number;
  tpag: string;
  amount: string;
  methodName: string | null;
  electronic: boolean;
  institutionCnpj: string | null;
  authorizationCode: string | null;
  pixEndToEnd: string | null;
};
export type FiscalPreview = {
  /**
   * Hash do que o usuário confere: cálculo + ambiente + provedor (+ na fábrica: destinatário
   * completo, pagamentos e referências). A emissão exige o MESMO hash.
   */
  hash: string;
  calc: CalcResult;
  use: FiscalUse;
  sourceId: string;
  sourceLabel: string;
  environment: FiscalEnvironment;
  provider: FiscalProviderCode;
  /** v7.1: emitente como vai na nota (cadastro da empresa) */
  issuer: {
    legalName: string;
    tradeName: string | null;
    cnpj: string;
    stateRegistration: string | null;
    crt: string | null;
    uf: string | null;
    city: string | null;
  };
  recipient: {
    name: string;
    document: string | null;
    uf: string | null;
    ieIndicator: string;
    email: string | null;
  };
  header: {
    nature: string | null;
    purpose: string | null;
    presence: string | null;
    direction: string | null;
    auxiliaryLayout: string | null;
    emissionFlow: string | null;
  };
  payments: PreviewPayment[];
  references: {
    kind: string;
    accessKey: string;
    documentId: string | null;
    number: number | null;
  }[];
  existingDocumentId: string | null;
  /** pode emitir agora (cálculo ok, provedor ligado, sem documento ativo) */
  canEmit: boolean;
};

// ---------------------------------------------------------------- cadastros fiscais (visões)
export const taxGroupSchema = z.object({
  code: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z0-9][A-Z0-9_.-]{0,29}$/, "Código com letras, números, ponto, hífen ou _"),
  name: z.string().trim().min(2).max(80),
  description: nullableText(500),
  status: z.enum(["active", "inactive"]).default("active"),
  version: z.number().int().optional(),
});
export type TaxGroup = Omit<z.output<typeof taxGroupSchema>, "version"> & {
  id: string;
  version: number;
  productCount: number;
};

export const recipientProfileInputSchema = z.object({
  name: z.string().trim().min(2).max(60),
  personKind: z.enum(["pf", "pj"]).nullable().default(null),
  ieIndicator: z.enum(["1", "2", "9"]).nullable().default(null),
  finalConsumer: z.boolean().nullable().default(null),
  ruralProducer: z.boolean().nullable().default(null),
  status: z.enum(["active", "inactive"]).default("active"),
  version: z.number().int().optional(),
});
export type FiscalRecipientProfile = Omit<
  z.output<typeof recipientProfileInputSchema>,
  "version"
> & {
  id: string;
  version: number;
};

export type FiscalSeriesView = z.output<typeof fiscalSeriesSchema> & {
  id: string;
  version: number;
  firstUsedAt: string | null;
};
export type PaymentInstitution = Omit<z.output<typeof paymentInstitutionSchema>, "version"> & {
  id: string;
  version: number;
};
export type PaymentTerminal = Omit<z.output<typeof paymentTerminalSchema>, "version"> & {
  id: string;
  institutionName: string;
  version: number;
};
export type PaymentMethodFiscal = Omit<z.output<typeof paymentMethodFiscalSchema>, "version"> & {
  paymentMethodId: string;
  paymentMethodName: string;
  kind: string;
  tpag: string;
  configured: boolean;
  version: number | null;
};
export type FiscalOperationUseView = {
  use: FiscalUse;
  label: string;
  systemType: FiscalSystemType;
  operationId: string | null;
  operationName: string | null;
};
export const operationUseSchema = z.object({ operationId: z.uuid().nullable() });

export const referenceRateSchema = z.object({
  code: refCode,
  tax: z.enum(["ibs_uf", "ibs_mun", "cbs", "is", "outro"]),
  description: nullableText(500),
  rate: pct,
  validFrom: isoDate,
  validTo: isoDate.nullable().default(null),
  version: z.number().int().optional(),
});
export type ReferenceRateView = Omit<z.output<typeof referenceRateSchema>, "version"> & {
  id: string;
  status: "rascunho" | "aprovada" | "arquivada";
  createdByEmail: string | null;
  preparedByEmail: string | null;
  approvedByEmail: string | null;
  approvedAt: string | null;
  /** aprovada por quem a preparou (permissão fiscal.matriz.aprovar_propria) */
  selfApproved: boolean;
  closeReason: string | null;
  version: number;
};

export const closeVigenciaSchema = z.object({
  validTo: isoDate,
  reason: z.string().trim().min(3, "Informe o motivo").max(500),
  version: z.number().int(),
});
export const approveRuleSchema = z.object({
  note: z.string().trim().max(1000).optional(),
  /** v7.1: sem escopo vale produção (como antes); a tela sempre pergunta */
  scope: z.enum(APPROVAL_SCOPES).default("producao"),
  version: z.number().int(),
});
/** v7.1: liberar para produção uma versão aprovada só para testes */
export const promoteRuleSchema = z.object({
  note: z.string().trim().max(1000).optional(),
  version: z.number().int(),
});
export const ruleValidationSchema = z.object({
  responsible: z.string().trim().min(2).max(120),
  crc: z.string().trim().min(3).max(30).nullable().default(null),
  date: isoDate,
  fileId: z.uuid().nullable().default(null),
  version: z.number().int(),
});
export const ruleVersionUpdateSchema = taxRuleVersionInputSchema.extend({
  version: z.number().int(),
});
export const ruleDuplicateSchema = z.object({
  code: taxRuleCreateSchema.shape.code,
  name: taxRuleCreateSchema.shape.name,
});
export const ruleQuerySchema = z.object({
  kind: z.enum(TAX_KINDS).optional(),
  status: z
    .enum(["todas", "rascunho", "testada", "aprovada", "vigente", "encerrada", "arquivada"])
    .default("todas"),
  q: z.string().trim().max(100).optional(),
});

export type RuleSummary = {
  id: string;
  kind: TaxKind;
  code: string;
  name: string;
  description: string | null;
  archived: boolean;
  version: number;
  versions: RuleVersionView[];
  /** v7.1: pendências fiscais da regra (abertas e resolvidas) */
  pendencies: FiscalPendencyView[];
};

// ---------------------------------------------------------------- pendências fiscais (v7.1)
export const FISCAL_PENDENCY_STATUSES = ["aberta", "resolvida"] as const;
export type FiscalPendencyStatus = (typeof FISCAL_PENDENCY_STATUSES)[number];
export const FISCAL_PENDENCY_SOURCE_LABEL: Record<"manual" | "carga_contador", string> = {
  manual: "Registrada manualmente",
  carga_contador: "Carga das regras do contador",
};
export type FiscalPendencyView = {
  id: string;
  ruleId: string | null;
  ruleCode: string | null;
  ruleName: string | null;
  productId: string | null;
  productCode: string | null;
  productName: string | null;
  code: string;
  title: string;
  description: string;
  source: "manual" | "carga_contador";
  status: FiscalPendencyStatus;
  openedByEmail: string | null;
  openedAt: string;
  resolvedByEmail: string | null;
  resolvedAt: string | null;
  resolution: string | null;
  version: number;
};
export const fiscalPendencyQuerySchema = z.object({
  status: z.enum(["aberta", "resolvida", "todas"]).default("aberta"),
});
export const fiscalPendencyCreateSchema = z
  .object({
    ruleId: z.uuid().nullable().default(null),
    productId: z.uuid().nullable().default(null),
    code: z
      .string()
      .trim()
      .toUpperCase()
      .regex(/^[A-Z0-9][A-Z0-9_]{1,39}$/, "Código com letras maiúsculas, números e _"),
    title: z.string().trim().min(3, "Informe o título").max(160),
    description: z.string().trim().min(3, "Descreva a pendência").max(4000),
  })
  .refine((d) => (d.ruleId === null) !== (d.productId === null), {
    message: "Escolha a regra OU o produto da pendência.",
    path: ["ruleId"],
  });
export const fiscalPendencyResolveSchema = z.object({
  resolution: z
    .string()
    .trim()
    .min(10, "Descreva a resolução: quem confirmou, quando e o que foi decidido.")
    .max(2000),
  version: z.number().int(),
});

export type TestCaseView = {
  id: string;
  name: string;
  input: SimulationInput;
  expected: z.output<typeof testCaseInputSchema>["expected"];
  status: "active" | "archived";
  lastRunAt: string | null;
  lastResult: "passou" | "falhou" | null;
  lastDetails: { differences: string[] } | null;
  version: number;
};

// ---------------------------------------------------------------- documentos (consulta)
export const fiscalDocumentQuerySchema = z.object({
  status: z.enum([...FISCAL_DOCUMENT_STATUSES, "todos", "pendencias"]).default("todos"),
  use: z.enum(FISCAL_USES).optional(),
  model: z.enum(["55", "65"]).optional(),
  from: isoDate.optional(),
  to: isoDate.optional(),
  salesOrderId: z.uuid().optional(),
  loadId: z.uuid().optional(),
  q: z.string().trim().max(100).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
export const xmlExportQuerySchema = z.object({
  from: isoDate,
  to: isoDate,
  model: z.enum(["55", "65"]).optional(),
});

/** Situação fiscal de um documento de negócio (venda, carregamento, acerto, devolução). */
export type FiscalLink = {
  use: FiscalUse;
  documentId: string;
  model: "55" | "65";
  series: number;
  number: number;
  status: FiscalDocumentStatus;
  accessKey: string | null;
};

// ---------------------------------------------------------------- devolução (visões)
export type SalesReturnItem = {
  id: string;
  line: number;
  salesOrderItemId: string;
  productId: string;
  productCode: string;
  productName: string;
  lotId: string | null;
  lotCode: string | null;
  quantity: string;
  unitPrice: string;
  total: string;
};
export type SalesReturn = {
  id: string;
  number: string;
  salesOrderId: string;
  salesOrderNumber: string;
  customerId: string;
  customerName: string;
  warehouseId: string;
  warehouseName: string;
  returnDate: string;
  reason: string;
  fiscalMode: "emissao_propria" | "nota_do_cliente" | "sem_documento_fiscal" | null;
  customerAccessKey: string | null;
  totalAmount: string;
  status: "rascunho" | "confirmado" | "estornado";
  reversalReason: string | null;
  version: number;
  items?: SalesReturnItem[];
  fiscal?: FiscalLink | null;
};
/** O que ainda pode voltar de cada item vendido (por lote). */
export type ReturnableLine = {
  salesOrderItemId: string;
  line: number;
  productId: string;
  productCode: string;
  productName: string;
  lotId: string | null;
  lotCode: string | null;
  sold: string;
  returnable: string;
  unitPrice: string;
};
export const salesReturnQuerySchema = z.object({
  status: z.enum(["rascunho", "confirmado", "estornado", "todas"]).default("todas"),
  salesOrderId: z.uuid().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
